import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'custom-multi-select',
  templateUrl: './custom-multi-select.component.html',
  standalone: false,
  styleUrls: ['./custom-multi-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomMultiSelectComponent),
      multi: true
    }
  ]
})
export class CustomMultiSelectComponent implements ControlValueAccessor, OnChanges {
  @Input() items: any[] = [];
  @Input() multiple: boolean = true;
  @Input() defaultValue: any[] = [];
  @Input() optionValueField: string = 'value';
  @Input() optionTextField: string = 'text';
  @Input() placeholder: string = 'Select options';
  @Input() disabled: boolean = false;

  @Output() select = new EventEmitter<any>();
  @Output() remove = new EventEmitter<any>();

  selectedItems: any[] = [];
  isOpen: boolean = false;
  searchTerm: string = '';

  private onChange = (value: any[]) => {};
  private onTouched = () => {};

  ngOnChanges(changes: SimpleChanges) {
    if (changes['defaultValue'] && changes['defaultValue'].currentValue) {
      this.selectedItems = [...(changes['defaultValue'].currentValue || [])];
    }
  }

  writeValue(value: any[]): void {
    this.selectedItems = value || [];
  }

  registerOnChange(fn: (value: any[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  toggleDropdown() {
    if (!this.disabled) {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.onTouched();
      }
    }
  }

  closeDropdown() {
    this.isOpen = false;
  }

  get filteredItems() {
    if (!this.searchTerm) {
      return this.items || [];
    }
    return (this.items || []).filter(item => 
      this.getDisplayText(item).toLowerCase().includes(this.searchTerm.toLowerCase())
    );
  }

  getDisplayText(item: any): string {
    return item[this.optionTextField] || item.toString();
  }

  getValue(item: any): any {
    return item[this.optionValueField] !== undefined ? item[this.optionValueField] : item;
  }

  isSelected(item: any): boolean {
    const itemValue = this.getValue(item);
    return this.selectedItems.some(selected => selected === itemValue);
  }

  selectItem(item: any) {
    const itemValue = this.getValue(item);
    
    if (!this.isSelected(item)) {
      if (this.multiple) {
        this.selectedItems = [...this.selectedItems, itemValue];
      } else {
        this.selectedItems = [itemValue];
        this.closeDropdown();
      }
      this.onChange(this.selectedItems);
      this.select.emit(itemValue);
    }
  }

  removeItem(itemValue: any) {
    this.selectedItems = this.selectedItems.filter(selected => selected !== itemValue);
    this.onChange(this.selectedItems);
    this.remove.emit(itemValue);
  }

  getSelectedDisplayText(): string {
    if (this.selectedItems.length === 0) {
      return this.placeholder;
    }
    
    if (this.selectedItems.length === 1) {
      const item = this.items.find(i => this.getValue(i) === this.selectedItems[0]);
      return item ? this.getDisplayText(item) : this.selectedItems[0];
    }
    
    return `${this.selectedItems.length} items selected`;
  }

  getDisplayTextForValue(value: any): string {
    const item = this.items.find(i => this.getValue(i) === value);
    return item ? this.getDisplayText(item) : value;
  }

  clearAll() {
    this.selectedItems = [];
    this.onChange(this.selectedItems);
  }

  onSearchChange() {
    // Filter is handled by get filteredItems()
  }
}